import type { EvaEndpoint } from '@springtree/eva-sdk-core-service';
import type { UseQueryOptions } from '@tanstack/react-query';
import { SvcGetApplicationConfiguration } from '@springtree/eva-services-core';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import { useCallback } from 'react';
import callEvaService from '@/core/services/eva/call-eva-service';
import { useEvaContext } from '@/ui/context/eva';

export type Variables = {
  organizationUnitId?: string;
};

const isCulture = (
  culture: unknown,
): culture is { CountryID: string; LanguageID: string } =>
  !!culture &&
  typeof culture === 'object' &&
  'CountryID' in culture &&
  'LanguageID' in culture &&
  typeof culture.LanguageID === 'string' &&
  typeof culture.CountryID === 'string';

export const queryKey = (variables: Variables) => [
  'eva-application-configuration',
  variables,
];

export const queryFn = async (endpoint: EvaEndpoint, variables: Variables) => {
  const response = await callEvaService(
    endpoint,
    SvcGetApplicationConfiguration,
    {
      OrganizationUnitID: variables.organizationUnitId,
    },
    {
      revalidate: 60 * 15, // 15 min
      cache: 'public', // Use this only when you know it data that can be shared across users
      requestedOrganizationUnitID: variables.organizationUnitId,
    },
  );

  if (!response || response.Error) {
    throw new Error(response?.Error?.Message);
  }

  const cultures = Array.isArray(response.Configuration?.ContentCultures)
    ? response.Configuration.ContentCultures.filter(isCulture)
    : [];
  const uniqueCultures = uniqBy(
    cultures,
    culture => culture.LanguageID + culture.CountryID,
  );

  return {
    ...(response.Configuration ?? {}),
    ContentCultures: uniqueCultures,
    OrganizationUnitCulture:
      typeof response.Configuration?.OrganizationUnitCulture === 'object' &&
      !Array.isArray(response.Configuration.OrganizationUnitCulture)
        ? {
            CountryID: response.Configuration.OrganizationUnitCulture
              ?.CountryID as string,
            LanguageID: response.Configuration.OrganizationUnitCulture
              ?.LanguageID as string,
          }
        : undefined,
  };
};

const useApplicationConfigurationQuery = (
  variables: Variables,
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof queryFn>>>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { endpoint } = useEvaContext();

  return useQuery({
    ...options,
    queryKey: queryKey(variables),
    queryFn: variables.organizationUnitId
      ? () => queryFn(endpoint, variables)
      : skipToken,
  });
};

export const useFetchApplicationConfigurationQuery = () => {
  const queryClient = useQueryClient();
  const { endpoint } = useEvaContext();

  const fetchQuery = useCallback(
    (variables: Variables) =>
      queryClient.fetchQuery({
        queryKey: queryKey(variables),
        queryFn: () => queryFn(endpoint, variables),
      }),
    [queryClient, endpoint],
  );

  return {
    fetchQuery,
  };
};

export default useApplicationConfigurationQuery;
